import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const SCORECARD_PARAMS = {
  cardsView: 'view',
  graphView: 'progressView',
  category: 'category',
  selectedCategories: 'categories',
  start: 'start',
  end: 'end',
  aggregateBy: 'aggregateBy',
} as const;

/** Parse the comma-separated `categories` param into a string[]. */
export const useSelectedCategoriesParam = (): [
  string[] | null,
  (next: string[]) => void,
] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const selected = useMemo(() => {
    const raw = searchParams.get(SCORECARD_PARAMS.selectedCategories);
    if (raw === null) return null; // null => "not set", fall back to server defaults
    return raw.length ? raw.split(',').map(decodeURIComponent) : [];
  }, [searchParams]);

  const setSelected = useCallback(
    (next: string[]) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          params.set(
            SCORECARD_PARAMS.selectedCategories,
            next.map(encodeURIComponent).join(','),
          );
          return params;
        },
        { replace: true }, // selection churn shouldn't spam browser history
      );
    },
    [setSearchParams],
  );

  return [selected, setSelected];
};

/**
 * Build a destination path that PRESERVES the current scorecard query string,
 * overriding only the params you pass in `overrides`. Use for every drill-down.
 */
export const useScorecardLinkBuilder = () => {
  const [searchParams] = useSearchParams();
  return useCallback(
    (pathname: string, overrides: Record<string, string | undefined> = {}) => {
      const params = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(overrides)) {
        if (value === undefined) params.delete(key);
        else params.set(key, value);
      }
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [searchParams],
  );
};
